import { get, isNil } from 'lodash-es'
import moment from 'moment-mini'

export function defineBackendValidation(attrs, i18n) {
  const errors = get(attrs, 'backend-validation-errors')

  if (!errors) {
    return []
  } else if (Array.isArray(errors)) {
    return errors.map((v) => i18n.t(v))
  } else {
    return [() => i18n.t(errors)]
  }
}

export function defineValidation(attrs, i18n, validateArray) {
  if (!get(attrs, 'validation')) return []

  const validationObj = {}
  const isDisabled = get(attrs, 'disabled')
  const getRequired = get(attrs, 'validation.required')
  const getGeoError = get(attrs, 'validation.geoError')
  const getPattern = get(attrs, 'validation.pattern')
  const getMaxCharLength = get(attrs, 'validation.maxCharLength')
  const getFutureDateAllowed = get(attrs, 'validation.futureDateAllowed')

  const getArray = validateArray

  if (isDisabled) {
    return []
  } else {
    const rules = get(attrs, 'validation')

    Object.keys(rules).forEach((ruleKey) => {
      switch (ruleKey) {
        case 'required':
          if (getRequired) {
            validationObj.required = (v) => {
              if (typeof v === 'string') v = v.trim()
              return (
                typeof v === 'number' ||
                Boolean(v) ||
                i18n.t(get(attrs, 'validation.requiredMessage'))
              )
            }
            break
          }
        case 'geoError':
          if (getGeoError) {
            validationObj.geoError = i18n.t(get(attrs, 'validation.geoErrorMessage'))
          }
          break
        case 'pattern':
          if (getPattern) {
            const useRegExp = new RegExp(get(attrs, 'validation.pattern'))
            validationObj.pattern = (v) =>
              !v || Boolean(v.match(useRegExp)) || i18n.t(get(attrs, 'validation.patternMessage'))
          }
          break
        case 'maxCharLength':
          if (getMaxCharLength) {
            validationObj.isMaxCharLength = (v) => {
              if (Array.isArray(v)) {
                return (
                  !v?.length ||
                  v.every((item) => item?.length <= getMaxCharLength) ||
                  i18n.t(get(attrs, 'validation.maxCharLengthMessage'))
                )
              } else {
                return (
                  !v ||
                  v?.length <= getMaxCharLength ||
                  i18n.t(get(attrs, 'validation.maxCharLengthMessage'))
                )
              }
            }
          }
          break
        case 'futureDateAllowed':
          if (getFutureDateAllowed === false) {
            const today = moment().format('YYYY-MM-DD')

            validationObj.futureDateAllowed = (v) => {
              if (!v) {
                return true
              } else {
                const isFutureDate = moment(v).isAfter(today)
                return !isFutureDate || i18n.t(get(attrs, 'validation.futureDateAllowedMessage'))
              }
            }
          }
          break
      }
    })

    if (!getArray) {
      const getMin = get(attrs, 'validation.min')
      const getMax = get(attrs, 'validation.max')
      const getMinLength = get(attrs, 'validation.minLength')
      const getMaxLength = get(attrs, 'validation.maxLength')

      if (getMin) {
        validationObj.isMin = (v) =>
          isNil(v) ||
          isNaN(parseFloat(v)) ||
          Number(v) >= getMin ||
          i18n.t(get(attrs, 'validation.minMessage'))
      }

      if (getMax) {
        validationObj.isMax = (v) =>
          isNil(v) ||
          isNaN(parseFloat(v)) ||
          Number(v) <= getMax ||
          i18n.t(get(attrs, 'validation.maxMessage'))
      }

      if (getMinLength) {
        validationObj.isMinLength = (v) =>
          !v || v.length >= getMinLength || i18n.t(get(attrs, 'validation.minLengthMessage'))
      }

      if (getMaxLength) {
        validationObj.isMaxLength = (v) =>
          !v || v.length <= getMaxLength || i18n.t(get(attrs, 'validation.maxLengthMessage'))
      }
    }

    if (getArray) {
      const getMin = get(attrs, 'validation.min')
      const getMax = get(attrs, 'validation.max')

      if (getMin) {
        validationObj.isMin = (v) =>
          !v || Boolean(v.length >= getMin) || i18n.t(get(attrs, 'validation.minMessage'))
      }

      if (getMax) {
        validationObj.isMax = (v) =>
          !v || Boolean(v.length <= getMax) || i18n.t(get(attrs, 'validation.maxMessage'))
      }

      if (getRequired) {
        validationObj.isAnArray = (v) =>
          !v || Boolean(v.length) || i18n.t(get(attrs, 'validation.requiredMessage'))
      }

      if (getMaxCharLength) {
        validationObj.isMaxCharLength = (v) =>
          !v ||
          v.length <= getMaxCharLength ||
          i18n.t(get(attrs, 'validation.maxCharLengthMessage'))
      }
    }

    const backendValidation = defineBackendValidation(attrs, i18n)
    const validationRules = Object.values(validationObj).filter(Boolean)

    return [...backendValidation, ...validationRules]
  }
}

export function definePhoneValidation(fields, i18n, isValid) {
  const validationObj = {}

  fields.forEach((field) => {
    const isDisabled = get(field, 'disabled')
    const getRequired = get(field, 'validation.required')
    const value = get(field, 'value')

    if (isDisabled) {
      return
    }

    if (getRequired) {
      validationObj.required = () => {
        return !!value?.trim() || i18n.t(get(field, 'validation.requiredMessage'))
      }
    }

    validationObj.isValidNum = () => isValid || i18n.t('talent.message.error.invalid.phone.number')
  })

  const backendValidation = defineBackendValidation(fields[1], i18n)
  const validationRules = Object.values(validationObj).filter(Boolean)

  return [...backendValidation, ...validationRules]
}

export function disableAdding(value, config) {
  const max = get(config, 'validation.max')
  return typeof max === 'number' && value.length >= max
}

export function disableRemoving(value, config) {
  const min = get(config, 'validation.min')
  const required = get(config, 'validation.required', false)

  return (
    (required && !(value === 0 ? true : value)) || (typeof min === 'number' && value.length <= min)
  )
}
